// plotName.ts — a plot's address, from the inherited MoM codec.
//
// This file used to hash lat/lon into three words from wordlists of my own.
// It worked, and it was wrong in the way that matters: it named ground nothing
// else had ever named. MoM has been naming Martian plots since 2021, and the
// codec is a BIJECTION over H3 resolution-8 cell ids — every cell exactly one
// name, every name exactly one cell, no collisions possible. A hash cannot
// promise that, and more importantly a hash of my own invention could never
// agree with a plot someone already owns.
//
// So the address is now: ground -> H3 res-8 cell -> MoM name.
//
// One number worth carrying: an H3 res-8 cell is 0.738 km2 on Earth and
// therefore 0.210 km2 on Mars, about 570 m across. That is the MoM parcel —
// the unit that has been bought and sold. Our rendering plots are 3.74 km2, so
// ONE OF OUR PLOTS SPANS ABOUT EIGHTEEN MoM PARCELS. The plot takes the name of
// the cell at its centre (its "seat"), and the panel says how many parcels it
// covers rather than pretending the two lattices are the same thing.
//
// (For the record: the datum registry's "hex_area_km2_default: 87.0" for Mars
// res-8 is out by a factor of ~414. Worth a canon fix.)

// h3-js is ~82 KB gzipped and only the Atlas needs it, so it loads with the
// tier rather than with the app. `ensureAddresses()` is awaited once when a
// plot loads; after that `plotAddress` is synchronous, which matters because
// the panel re-renders many times a second during a season sweep and was
// computing H3 on every one of them.
type H3 = typeof import('h3-js');
let h3: H3 | null = null;

export async function ensureAddresses(): Promise<void> {
  if (!h3) h3 = await import('h3-js');
}

import { GetHexName, GetHexId } from './momName.ts';
import { R_EARTH_M } from './hexOverlay.ts';
import { wrap180HalfOpen } from '../math/angles.ts';

/** An H3 res-8 cell's average area on Earth, km2 (h3-js's own figure). */
const PARCEL_AREA_KM2_ON_EARTH = 0.738;

/** A MoM parcel's (H3 res-8) area in km2, scaled to a body of this radius. */
export function parcelAreaKm2(radiusM: number): number {
  return PARCEL_AREA_KM2_ON_EARTH * (radiusM / R_EARTH_M) ** 2;
}

export interface PlotAddress {
  /** "Spiny Snarl Emu" — the MoM name for this ground. */
  name: string;
  /** The H3 resolution-8 cell, as MoM stores it. This is the real key. */
  cell: string;
  /** Cell centre, which is the canonical position of the parcel. */
  lat: number;
  lon: number;
}

/**
 * The address of a piece of ground. Pure, and identical to MoM's.
 *
 * Always resolution 8, never the registry's `default_h3_resolution`: the
 * codec's mask is res-8 by construction (`momName.ts`'s `H3Res8Bits`), so a
 * claim key and a drawn hex-overlay cell are different resolutions on purpose,
 * since the overlay draws whichever (coarser) rung the altitude selects; this
 * names the res-8 parcel at that cell's own centre, its "seat".
 */
export function plotAddress(latDeg: number, lonDeg: number): PlotAddress {
  if (!h3) throw new Error('call ensureAddresses() before plotAddress()');
  // h3-js takes longitude in -180..180.
  const lon = wrap180HalfOpen(lonDeg);
  const cell = h3.latLngToCell(latDeg, lon, 8);
  const [clat, clon] = h3.cellToLatLng(cell);
  return {
    name: GetHexName(BigInt(`0x${cell}`)),
    cell,
    lat: clat,
    lon: ((clon % 360) + 360) % 360,
  };
}

/** Convenience: just the name. */
export function plotName(latDeg: number, lonDeg: number): string {
  return plotAddress(latDeg, lonDeg).name;
}

/**
 * The address of a cell the server named, rather than of ground picked here.
 *
 * A `CLAIM#` row carries the cell and nothing about where it is, so a claim
 * read back from the server is placed at its own parcel centre - the same
 * point `plotAddress` calls canonical. Null before h3-js has loaded.
 */
export function addressOfCell(cell: string): PlotAddress | null {
  if (!h3) return null;
  try {
    const [clat, clon] = h3.cellToLatLng(cell);
    return {
      name: GetHexName(BigInt(`0x${cell}`)),
      cell,
      lat: clat,
      lon: ((clon % 360) + 360) % 360,
    };
  } catch {
    return null;
  }
}

/** "Spiny Snarl Emu" -> the ground it names. Exact inverse. */
export function addressToCell(name: string): string | null {
  const id = GetHexId(name);
  return id < 0n ? null : id.toString(16);
}

/** How many MoM parcels a plot of this area covers, on a body of this radius. */
export function parcelsIn(areaKm2: number, radiusM: number): number {
  return Math.max(1, Math.round(areaKm2 / parcelAreaKm2(radiusM)));
}
